/**
 * Service for invalidating organization-related queries
 * Used by the organization store to invalidate queries on organization switch
 */

import { log } from '../utils/errors/logger';
import { QueryClient } from '@tanstack/react-query';

class QueryInvalidationService {
  private queryClient: QueryClient | null = null;

  setQueryClient(client: QueryClient) {
    this.queryClient = client;
  }

  getQueryClient(): QueryClient | null {
    return this.queryClient;
  }

  clearAllQueries() {
    if (this.queryClient) {
      this.queryClient.clear();
      log.debug('Cleared all TanStack Query cache via QueryInvalidationService');
    } else {
      log.warn('QueryClient not available for cache clearing');
    }
  }

  async invalidateOrganizationQueries(newOrgId: string, previousOrgId?: string) {
    if (!this.queryClient) {
      log.warn('QueryClient not set in QueryInvalidationService');
      return;
    }

    // Invalidate all organization-dependent queries
    await this.queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        if (!Array.isArray(queryKey)) {return false;}

        // First element is usually the resource type, second is often the org ID
        const [resourceType, orgId] = queryKey;

        // Invalidate organization-dependent queries
        const orgDependentKeys = [
          'groups', 'allGroups', 'myGroups',
          'users', 'organizationUsers', 
          'documents', 'documentSources',
          'conversations', 'conversation',
          'chatConfig', 'embeddingConfig', 'textExtractionConfig', 'smtpConfig',
          'organizationStats', 'organizationSettings', 'authProviders',
          'performance', 'slowQueries', 'system-status'
        ];

        // Check if this is an organization-dependent query
        const isOrgDependent = orgDependentKeys.includes(resourceType as string);
        
        // Check if query contains org IDs anywhere in the key
        const containsOrgId = queryKey.includes(previousOrgId) || queryKey.includes(newOrgId);
        
        // Special handling for queries with org ID as second element (like ['users', orgId])
        const hasOrgIdAsSecondElement = orgId === previousOrgId || orgId === newOrgId;

        return isOrgDependent || containsOrgId || hasOrgIdAsSecondElement;
      }
    });

    log.debug(`Query invalidation completed for organization switch: ${previousOrgId} -> ${newOrgId}`);
  }
}

export const queryInvalidationService = new QueryInvalidationService();